import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {  Observable, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { HttpParams } from '@angular/common/http';
import {
  ApiDashboardResponse,
  DashboardBankAccount,
  DashboardData,
  DashboardPoliza,
  DashboardTransaccion,
  UrlResponse,
} from '../interfaces/dashboard.interface';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = `${environment.int365_dom}/api/v1/Dashboard/dashboard`;
  private cachedData: DashboardData | null = null;
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutos en milisegundos
  private conversionRate: number = 0;
  
  // Propiedades para detectar cambios de usuario
  private cachedToken: string | null = null;
  private cachedProducerNumber: number | null = null;

  // Señal para almacenar las cuentas bancarias
  public bankAccounts = signal<DashboardBankAccount[]>([]);


  

  constructor(private http: HttpClient, private authService: AuthService) {}

  getConversionRate(): number {
    return this.conversionRate;
  }

  getDashboardData(): Observable<DashboardData> {
    const now = Date.now();
    
    // Verificar si hay un nuevo login (cambio de token o usuario)
    if (this.hasUserChanged()) {
      this.clearCache();
    }

    // Si tenemos datos en caché y no han pasado 30 minutos
    if (this.cachedData && now - this.lastFetch < this.CACHE_DURATION) {
      return of(this.cachedData);
    }

    if (!this.authService.isAuthenticated()) {
      throw new Error('Usuario no autenticado');
    }

    const token = this.authService.getToken();

    if (!token) {
      throw new Error('No token found');
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const producerNumber = this.authService.getProducerNumber();
    const params = new HttpParams().set('producerNumber', producerNumber || '');

    return this.http
      .get<ApiDashboardResponse>(this.apiUrl, { headers, params })
      .pipe(
        map((response) => {
          // Guardar la tasa de conversión
          this.conversionRate = response.data.conversionRate.rate;

          // Guardar las cuentas bancarias
          this.bankAccounts.set(response.data.bankAccounts || []);


          const transformedData = this.transformDashboardData(response.data);
          
          // Actualizar caché y timestamp
          this.cachedData = transformedData;
          this.lastFetch = now;
          
          // Actualizar información del usuario actual
          this.updateUserInfo();
          
          return transformedData;
        })
      );
  }

  // Método para verificar si el usuario ha cambiado
  private hasUserChanged(): boolean {
    const currentToken = this.authService.getToken();
    const currentProducerNumber = this.authService.getProducerNumber();
    
    // Si es la primera vez o si cambió el token o el producer number
    if (
      this.cachedToken !== currentToken ||
      this.cachedProducerNumber !== currentProducerNumber
    ) {
      return true;
    }
    
    return false;
  }

  // Método para actualizar la información del usuario actual
  private updateUserInfo(): void {
    this.cachedToken = this.authService.getToken();
    this.cachedProducerNumber = this.authService.getProducerNumber();
  }

  // Método para limpiar el caché
  private clearCache(): void {
    this.cachedData = null;
    this.lastFetch = 0;
  }

  // Método para forzar una actualización de los datos
  forceRefresh(): Observable<DashboardData> {
    this.clearCache();
    return this.getDashboardData();
  }

  private transformDashboardData(
    data: ApiDashboardResponse['data']
  ): DashboardData {
    if (!data)
      return {
        polizas: [],
        movimientos: [],
        url: null,
      };

    return {
      polizas: this.transformPolizas(data.header),
      movimientos: this.transformMovimientos(data.transactions),
      url: this.transformUrl(data.url),
    };
  }

  //Transforma los datos Dashboard
  private transformPolizas(
    header: ApiDashboardResponse['data']['header']
  ): DashboardPoliza[] {
    if (
      !header ||
      typeof header.activePolicies === 'undefined' ||
      typeof header.collectionTotal === 'undefined' ||
      typeof header.renovatedPolicies === 'undefined'
    ) {
      console.error('Datos incompletos en la respuesta del dashboard:', header);
      return []; // Retorna un arreglo vacío si los datos no están presentes
    }

    return [
      {
        activas: [
          { name: 'Total pólizas activas' },
          { monto: header.activePolicies?.toString() || '0' },
        ],
      },
      {
        prima: [
          { name: 'Prima total cobrada' },
          {
            monto: `$ ${
              header.collectionTotal?.toLocaleString('en-US', {
                minimumFractionDigits: 2,
              }) || '0.00'
            }`,
          },
        ],
      },
      {
        renovadas: [
          { name: 'Pólizas renovadas' },
          { monto: header.renovatedPolicies?.toString() || '0' },
        ],
      },
    ];
  }

  private transformMovimientos(
    transactions: ApiDashboardResponse['data']['transactions']
  ): DashboardTransaccion[] {
    if (!transactions) return [];

    return transactions.map((trans) => ({
      polizaNo: this.formatearPoliza(trans.policyNumber || ''),
      primaTotalRD: `$ ${
        trans.premium?.toLocaleString('en-US', {
          minimumFractionDigits: 2,
        }) || '0.00'
      }`,
      tipoTransaccion: trans.description,
      monto: trans.value
        ? `$ ${Number(trans.value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
          })}`
        : ` $ ${'0.00'}`,
    }));
  }

  private formatearPoliza(poliza: string): string {
    const str = poliza.toString();
    if (str.length >= 10) {
      return `${str.slice(0, 1)}-${str.slice(1, 2)}-${str.slice(
        2,
        5
      )}-${str.slice(5)}`;
    }
    return str;
  }

  private transformUrl(url: ApiDashboardResponse['data']['url']): UrlResponse {
    return { url: url.url };
  }
}
